import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Check, Clock, Shield, Euro, MapPin, Star } from 'lucide-react';
import { seoData } from '../data/seoData';
import { stadtteile } from '../data/stadtteile';
import SchemaOrg from '../components/SchemaOrg';
import GoogleReviews from '../components/GoogleReviews';
import GoogleBusinessPosts from '../components/GoogleBusinessPosts';
import ADACPartner from '../components/ADACPartner';

const LandingPage = () => {
  const seo = seoData['/schluesseldienst-dortmund/'];
  const phone = '+4923139085116';
  const displayPhone = '0231 39085116';
  const whatsapp = `https://wa.me/4923139085116?text=Hallo, ich benötige Hilfe beim Schlüsseldienst`;

  const faqData = [
    {
      question: 'Was kostet eine Türöffnung in Dortmund?',
      answer: 'Unsere Festpreise: Mo-Fr 08-20 Uhr: 119 €, Sa-So: 199 €. Anfahrt ist bereits inklusive. Keine versteckten Kosten.'
    },
    {
      question: 'Wie schnell sind Sie vor Ort?',
      answer: 'In der Regel sind wir innerhalb von 15-30 Minuten bei Ihnen in Dortmund, abhängig vom Stadtteil und Verkehr.'
    },
    {
      question: 'Wird die Tür beschädigt?',
      answer: 'Nein, wir verwenden schonende Öffnungstechniken. In über 95% der Fälle öffnen wir ohne Beschädigung von Tür oder Schloss.'
    },
    {
      question: 'Welche Zahlungsarten akzeptieren Sie?',
      answer: 'Wir akzeptieren Bar, EC-Karte und Kreditkarte für Ihre Bequemlichkeit.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href="https://schluesseldienst.services/schluesseldienst-dortmund/" />
      </Helmet>
      <SchemaOrg type="website" data={{ faqItems: faqData }} />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 to-amber-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Schlüsseldienst Dortmund – Festpreis & schnelle Hilfe
            </h1>
            
            {/* Answer Card */}
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border-2 border-orange-200">
              <p className="text-lg text-gray-700 mb-4">
                Schnelle Türöffnung in Dortmund: <strong>Mo–Fr 08–20: 119 €, Sa–So: 199 €</strong>. Anfahrt inklusive, Kartenzahlung möglich. Jetzt anrufen: <strong>{displayPhone}</strong>
              </p>
              <div className="flex flex-wrap justify-center gap-3 mb-4">
                <a
                  href={`tel:${phone}`}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-bold transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                  data-testid="hero-call-button"
                >
                  <Phone size={20} />
                  Jetzt anrufen
                </a>
                <a
                  href={whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full font-bold transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                  data-testid="hero-whatsapp-button"
                >
                  <MessageCircle size={20} />
                  WhatsApp
                </a>
              </div>
              <div className="text-center pt-3 border-t border-gray-200">
                <ADACPartner variant="inline" />
              </div>
            </div>

            {/* Key Facts */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center justify-center gap-2 bg-white rounded-lg p-3 shadow">
                <Clock className="text-orange-600" size={20} />
                <span>Schnell vor Ort</span>
              </div>
              <div className="flex items-center justify-center gap-2 bg-white rounded-lg p-3 shadow">
                <Shield className="text-orange-600" size={20} />
                <span>Schonende Öffnung</span>
              </div>
              <div className="flex items-center justify-center gap-2 bg-white rounded-lg p-3 shadow">
                <Euro className="text-orange-600" size={20} />
                <span>Faire Festpreise</span>
              </div>
              <div className="flex items-center justify-center gap-2 bg-white rounded-lg p-3 shadow">
                <MapPin className="text-orange-600" size={20} />
                <span>Alle PLZ Dortmund</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vorteile */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Ihre Vorteile auf einen Blick</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              'Transparente Festpreise ohne versteckte Kosten – Sie wissen vorher, was Sie zahlen',
              'Schnelle Anfahrt in 15-30 Minuten in allen Dortmunder Stadtteilen',
              'Schonende Öffnungstechniken schützen Ihre Tür und Ihr Schloss',
              'Professionelle Techniker mit langjähriger Erfahrung und modernem Werkzeug',
              'Bargeldlose Zahlung möglich: EC-Karte und Kreditkarte werden akzeptiert',
              'Seriöses lokales Unternehmen aus Dortmund – keine Abzocker-Hotline'
            ].map((vorteil, i) => (
              <div key={i} className="flex gap-3 items-start bg-orange-50 p-4 rounded-lg">
                <Check className="text-orange-600 flex-shrink-0" size={24} />
                <p>{vorteil}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ADAC Partner Sektion */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <ADACPartner variant="full" />
          </div>
        </div>
      </section>

      {/* Preistabelle */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Unsere Festpreise</h2>
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-orange-600 text-white">
                <tr>
                  <th className="p-4 text-left">Zeitfenster</th>
                  <th className="p-4 text-left">Leistung</th>
                  <th className="p-4 text-right">Preis</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-4 font-semibold">Mo–Fr 08–20 Uhr</td>
                  <td className="p-4">Tür zugefallen</td>
                  <td className="p-4 text-right text-xl font-bold text-orange-600">119 €</td>
                </tr>
                <tr className="border-b bg-orange-50">
                  <td className="p-4 font-semibold">Sa–So</td>
                  <td className="p-4">Tür zugefallen</td>
                  <td className="p-4 text-right text-xl font-bold text-orange-600">199 €</td>
                </tr>
              </tbody>
            </table>
            <div className="p-4 bg-gray-50 text-sm text-gray-600">
              <p className="mb-2"><strong>Inklusive:</strong> Anfahrt, Arbeitszeit, Mehrwertsteuer</p>
              <p><strong>Zahlungsarten:</strong> Bar, EC-Karte, Kreditkarte</p>
            </div>
          </div>
          <div className="text-center mt-8">
            <Link to="/preise" className="text-orange-600 hover:text-orange-700 font-semibold underline">
              Alle Preise und Beispielrechnungen →
            </Link>
          </div>
        </div>
      </section>

      {/* Stadtteile */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Wir sind in ganz Dortmund für Sie da</h2>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {stadtteile.map((st) => (
                <Link
                  key={st.slug}
                  to={`/schluesseldienst-dortmund-${st.slug}`}
                  className="bg-orange-50 hover:bg-orange-100 p-3 rounded-lg text-center transition-colors text-sm"
                >
                  {st.name}
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/einsatzgebiete" className="text-orange-600 hover:text-orange-700 font-semibold underline">
                Alle Einsatzgebiete anzeigen →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Häufig gestellte Fragen</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqData.map((faq, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-bold mb-3 text-gray-900">{faq.question}</h3>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews & Social Proof */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Was unsere Kunden sagen
              </h2>
              <p className="text-lg text-gray-600">
                Über 500 zufriedene Kunden haben uns auf Google bewertet
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <GoogleReviews variant="full" />
              </div>
              <div>
                <GoogleBusinessPosts />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links - Important for SEO */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Main Services Links */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-center mb-6 text-gray-900">
                Unsere Leistungen im Überblick
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Link 
                  to="/preise" 
                  className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-all border-2 border-transparent hover:border-orange-600"
                >
                  <Euro className="w-8 h-8 text-orange-600 mb-3" />
                  <h3 className="text-lg font-bold mb-2">Preise & Kosten</h3>
                  <p className="text-gray-600 text-sm">Transparente Festpreise für alle Dienstleistungen</p>
                </Link>
                <Link 
                  to="/einsatzgebiete" 
                  className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-all border-2 border-transparent hover:border-orange-600"
                >
                  <MapPin className="w-8 h-8 text-orange-600 mb-3" />
                  <h3 className="text-lg font-bold mb-2">Alle Stadtteile</h3>
                  <p className="text-gray-600 text-sm">In allen 52 Stadtteilen für Sie da</p>
                </Link>
                <Link 
                  to="/ratgeber" 
                  className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-all border-2 border-transparent hover:border-orange-600"
                >
                  <Shield className="w-8 h-8 text-orange-600 mb-3" />
                  <h3 className="text-lg font-bold mb-2">Ratgeber</h3>
                  <p className="text-gray-600 text-sm">Tipps & Wissen rund um Schlösser und Sicherheit</p>
                </Link>
              </div>
            </div>

            {/* Top Districts Links */}
            <div>
              <h3 className="text-xl font-bold text-center mb-6 text-gray-900">
                Schlüsseldienst in Ihrem Stadtteil
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                {stadtteile.slice(0, 12).map(stadtteil => (
                  <Link
                    key={stadtteil.slug}
                    to={`/schluesseldienst-dortmund-${stadtteil.slug}`}
                    className="bg-white px-4 py-2 rounded-full text-sm font-medium text-gray-700 hover:bg-orange-600 hover:text-white transition-all shadow hover:shadow-md"
                  >
                    {stadtteil.name}
                  </Link>
                ))}
              </div>
              <div className="text-center mt-6">
                <Link
                  to="/einsatzgebiete"
                  className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-semibold"
                >
                  Alle 52 Stadtteile anzeigen →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="py-16 bg-gradient-to-br from-orange-600 to-amber-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ausgesperrt? Wir helfen sofort!</h2>
          <p className="text-xl mb-8">Rufen Sie uns an – wir sind schnell bei Ihnen.</p>
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-full text-xl font-bold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl"
            data-testid="bottom-cta-call"
          >
            <Phone size={24} />
            {displayPhone}
          </a>
        </div>
      </section>
    </>
  );
};

export default LandingPage;
